import type { IInitCartStore } from "../interface/cart";
import type { IInitCustomer } from "../interface/customer";
import { initCart } from "./cart";

/**
 * localStorage 에는 서버에서 복구할 수 없는 식별자만 남긴다 — 카트는 id, 고객은 토큰.
 *
 * 이전에는 cart/customer 전체를 통째로 직렬화했는데, customerQuery 가 orders(first: 250) →
 * lineItems(first: 100) → variant/product/이미지까지 펼쳐오기 때문에 주문이 많은 고객은
 * localStorage 한도(~5MB)에 실제로 닿았다. 게다가 스토어 키가 언어별(`${lang}-customer`)이라
 * 언어를 오갈수록 같은 크기의 스냅샷이 3벌까지 쌓인다.
 *
 * 저장을 줄인 대신 부팅 시 useSessionRehydrate 가 id/토큰으로 서버에서 다시 채운다.
 * 두 가지는 반드시 한 세트로 적용해야 한다.
 *
 * 옛 스냅샷 정리는 migrate 가 아니라 merge 로 한다. zustand 의 migrate 는
 *   typeof persisted.version === "number" && persisted.version !== options.version
 * 일 때만 호출되는데, 이미 배포돼 있는 값에는 version 필드가 없어서(undefined) 조건이 거짓이라
 * 정작 걸러야 할 옛 스냅샷에는 영영 걸리지 않는다. merge 는 매 하이드레이션마다 무조건 돈다.
 */

/**
 * 기본 merge 는 저장된 top-level 을 그대로 덮어써서 옛 스냅샷의 cart 전체가 메모리로 들어온다.
 * 소비처들이 cart.lines.edges / cart.cost.totalAmount 를 바로 읽으므로 초기 형태 위에 id 만 얹는다.
 * cart 외에 저장한 값(sunlove isPreorder 처럼 서버에서 복구할 수 없는 플래그)은 그대로 둔다.
 */
export function mergeCart<S extends IInitCartStore>(persisted: unknown, current: S): S {
  const saved = persisted as (Partial<S> & Partial<IInitCartStore>) | undefined;
  if (!saved) return current;

  return { ...current, ...saved, cart: { ...initCart.cart, id: saved.cart?.id ?? "" } };
}

/**
 * customer 는 토큰류만 남긴다. 프로필/주소/주문은 부팅 시 다시 조회하므로 저장돼 있어도 버린다.
 *
 * 버리는 게 용량 문제만은 아니다. 옛 스냅샷의 customer 가 메모리로 들어오면 토큰이 없는데도
 * customer.id 만 있는 상태가 되고, App 의 buyerIdentity 검사가 매번 불일치로 판정해
 * 카트를 무한히 새로 만든다.
 */
export function mergeCustomer<S extends IInitCustomer>(persisted: unknown, current: S): S {
  const saved = persisted as Record<string, unknown> | undefined;
  if (!saved) return current;

  const { customer, caaCustomer, ...rest } = saved;

  return { ...current, ...(rest as Partial<S>) };
}
